import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Container,
  Grid,
  Segment
} from 'semantic-ui-react'
import { AxiosResponse } from 'axios'
// Local
import {
  API_URL,
  CONDITION,
  DISPLAY_MODE
} from '../../../constant'
import {
  isEmbeddedView,
  isSmartView
} from '../../common/currentUserUtils'
import { api } from '../../../action/api'
import PatientSnapshotWarning from '../common/patientSnapshotWarning'
import PatientSnapshotEncounterConditions from './patientSnapshotEncounterConditions'
import PatientSnapshotEncounterLeft from './patientSnapshotEncounterLeft'
import {
  EncounterConditionDisplay,
  PatientType
} from '../../authenticated/types'
import { RootState } from '../../../store'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

/**
 * Patient snapshot provides a read-only view of an encounter that is
 * effectively divorced from the encounter specifics. The goal is to provide
 * basic patient information and Rules Engine results. The initial
 * implementation of the patient snapshot is similar to the Clinical Admin
 * view of the encounter
 */

type Match = {
  path: string
  params: {
    id: string
  }
}

type Props = ReduxStateProps & {
  conditionGroups?: keyof typeof CONDITION.TYPE | undefined
  match: Match
}

function PatientSnapshot(
  props: Props
): JSX.Element | null {
  const {
    conditionGroups,
    currentUser,
    match
  } = props

  const [
    encounterIdState,
    setEncounterIdState
  ] = useState<string>(match.params.id)

  const [
    encounterDetailState,
    setEncounterDetailState
  ] = useState<PatientType | null>(null)

  const [
    activeConditionIdState,
    setActiveConditionIdState
  ] = useState<string | null>(null)

  const fetchEncounter = (encounterId: string): void => {
    const url = `${API_URL.GET_ENCOUNTER_DETAIL_BY_ID_WITH_ROLE(currentUser.type)}${encounterId}`
    // const url = API_URL.GET_ENCOUNTER_DETAIL_BY_ID + encounterId
    api.get(url).then((response: AxiosResponse<PatientType>) => {
      setEncounterIdState(encounterId)
      setEncounterDetailState(response.data)
    })
  }

  useEffect(() => {
    fetchEncounter(encounterIdState)
  }, [])

  const onConditionActivate = (
    condition: EncounterConditionDisplay | string
  ): void => {
    if (condition === null) {
      setActiveConditionIdState(null)
    } else {
      const id = typeof (condition) === 'string'
        ? condition
        : condition.id
      setActiveConditionIdState(id)
    }
  }

  const onConditionDeactivate = () => {
    setActiveConditionIdState(null)
  }

  const onConditionRead = (
    condition: EncounterConditionDisplay | string
  ): void => {
    if (
      typeof condition === 'object' &&
      condition !== null &&
      'id' in condition
    ) {
      api.post(
        API_URL.AUDIT(
          'READ',
          'ENCOUNTER_CONDITION',
          condition.id
        ),
        {}
      )
    }
  }

  const onConditionAction = (
    action: any,
    condition: EncounterConditionDisplay | string
  ): void => {
    if (action === CONDITION.ACTION.ACTIVATE) {
      onConditionActivate(condition)
    }
    if (action === CONDITION.ACTION.DEACTIVATE) {
      onConditionDeactivate()
    }
    if (action === CONDITION.ACTION.READ) {
      onConditionRead(condition)
    }
  }

  const renderSnapshot = (detail: PatientType) => {
    const key = (new Date()).getTime()
    let mode = null

    if (isSmartView(match.path)) {
      mode = DISPLAY_MODE.SMART
    }

    if (isEmbeddedView(match.path)) {
      mode = DISPLAY_MODE.EMBEDDED
    }

    return (
      <div className="header-padding">
        <Container>
          <SafeSegment
            attached
            className="no-margin"
          >
            <PatientSnapshotWarning encounterDetail={ detail } />
            <SafeGrid stackable>
              <Grid.Row>
                <Grid.Column width={ 4 }>
                  <PatientSnapshotEncounterLeft encounterDetail={ detail } />
                </Grid.Column>
                <Grid.Column width={ 12 }>
                  <PatientSnapshotEncounterConditions
                    key={ `encounter-conditions-${key}` }
                    currentUser={ currentUser }
                    mode={ mode }
                    encounterDetail={ detail }
                    conditionGroups={ conditionGroups }
                    activeConditionId={ activeConditionIdState }
                    onConditionAction={ onConditionAction }
                  />
                </Grid.Column>
              </Grid.Row>
            </SafeGrid>
          </SafeSegment>
        </Container>
      </div>
    )
  }

  return (
    encounterDetailState
      ? renderSnapshot(encounterDetailState)
      : null
  )
}

export default connect(mapStateToProps)(PatientSnapshot)
